import { client } from "../../client";
import {
  BillRequestStatus,
  BillRequestType,
  TransactionStatus,
  UIServerResponse,
  TicketInfo,
} from "..";

export const FINANCIAL_ACTIVITY_TYPES = [
  "Payment",
  "DunningCharge",
  "Deposit",
  "Repayment",
  "WriteOff",
  "WriteOn",
  "CreditWriteOff",
  "AccountCredit",
  "AccountDebit",
  "OutgoingBalanceTransfer",
  "IncomingBalanceTransfer",
  "PaygTransfer",
] as const;

export const AGREEMENT_STATUSES = ["Created", "Cancelled", "Ended"] as const;
export type AgreementStatus = typeof AGREEMENT_STATUSES[number];

const ACTIVITY_TYPES = [
  "Ticket",
  "Bill",
  "ManualBill",
  "Credit",
  "Note",
  "WrapUpNote", // <== Demand
  "Communication",
  "Agreement",
  ...FINANCIAL_ACTIVITY_TYPES,
] as const;

export type ActivityType = typeof ACTIVITY_TYPES[number];

export const FILTER_TYPES = [
  "Agreement",
  "Bill",
  "Communication",
  "DemandNote",
  "Financial",
  "Ticket",
];

export type FilterType = typeof FILTER_TYPES[number];

export const getActivityTypeDisplayName = (type: ActivityType) => {
  if (
    FINANCIAL_ACTIVITY_TYPES.includes(
      type as typeof FINANCIAL_ACTIVITY_TYPES[number],
    )
  ) {
    return "Financial";
  }
  if (type === "Note" || type === "WrapUpNote") {
    return "Wrap-up / Notes";
  }
  if (type === "ManualBill" || type === "Credit") {
    return "Bill";
  }
  return type;
};

export interface BaseActivity {
  id: string;
  type: ActivityType;
  user: string | null;
  activityDttm: string | null;
}

export interface TicketActivity extends BaseActivity {
  ticketInfo: TicketInfo;
}
export interface BillActivity extends BaseActivity {
  billNumber: string;
  grossAmount: string;
  netAmount: string;
  currency: string;
  requestType: BillRequestType;
  requestStatus: BillRequestStatus;
  transactionStatus: TransactionStatus;
  dueDt: string;
  isFinal: boolean;
  fromDttm: string;
  toDttm: string;
}

export interface ManualBillActivity extends BaseActivity {
  billNumber: string;
  amount: string;
  currency: string;
  requestStatus: BillRequestStatus;
  dueDt: string;
}

export interface CreditActivity extends BaseActivity {
  creditNumber: string;
  billId: string | null;
  grossAmount: string;
  netAmount: string;
  currency: string;
  transactionStatus: TransactionStatus;
  fromDttm: string | null;
  toDttm: string | null;
}

export interface FinancialActivity extends BaseActivity {
  amount: string;
  currency: string;
  isCredit: boolean;
  isCancellation: boolean;
  transactionStatus: TransactionStatus;
  financialEntityId: string | null;
}

export interface NoteActivity extends BaseActivity {
  modifiedDttm: string;
  summary: string;
  subject: string;
}

export interface DemandActivity extends BaseActivity {
  category: string;
  subcategory: string;
  content: string;
}

export interface CommunicationActivity extends BaseActivity {
  commMethods: string[];
  commType: string;
}

export interface AgreementActivity extends BaseActivity {
  agreementId: string;
  agreementNumber: string;
  agreementStatus: AgreementStatus;
}

export type Activity =
  | BaseActivity
  | TicketActivity
  | BillActivity
  | ManualBillActivity
  | CreditActivity
  | FinancialActivity
  | NoteActivity
  | DemandActivity
  | CommunicationActivity
  | AgreementActivity;

type ActivitiesResponse = UIServerResponse<Activity[]>;

export const getAccountActivities = async (
  key: string,
  {
    page,
    size,
    accountNumber,
    type,
  }: { page: number; size: number; accountNumber: string; type?: FilterType },
) => {
  const params: any = { page: String(page), size: String(size) };
  if (type) {
    params.type = type;
  }
  const response = await client.get<ActivitiesResponse>(
    `accounts/${accountNumber}/activities?${new URLSearchParams(
      params,
    ).toString()}`,
  );
  return {
    results: response?.data?.results,
    totalCount: response?.headers["x-total-count"],
  };
};
